import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useDropdownPosition from '../../hooks/useDropdownPosition';
import useAiSuggestions from '../../hooks/useAiSuggestions';
import { GENERATE_INSTRUCTION_SUGGESTIONS } from '../../lib/graphql';
import SuggestionChips from './SuggestionChips';

export type AiGenerateAction =
  | 'generate'
  | 'generateWithInstructions'
  | 'rephrase'
  | 'rephraseWithInstructions'
  | 'openInChat';

interface AiGenerateDropdownButtonProps {
  onAction: (action: AiGenerateAction, instructions?: string) => void;
  isLoading?: boolean;
  hasContent?: boolean;
  disabled?: boolean;
  fieldLabel?: string;
  className?: string;
  showRephraseOptions?: boolean;
  showChatOption?: boolean;
  size?: 'sm' | 'md';
  bookId?: string;
}

export default function AiGenerateDropdownButton({
  onAction,
  isLoading = false,
  hasContent = false,
  disabled = false,
  fieldLabel: _fieldLabel = '',
  className = '',
  showRephraseOptions = true,
  showChatOption = true,
  size = 'md',
  bookId,
}: AiGenerateDropdownButtonProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showInstructionsInput, setShowInstructionsInput] = useState<
    'generate' | 'rephrase' | null
  >(null);
  const [instructions, setInstructions] = useState('');
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  useDropdownPosition(dropdownRef, isOpen, 'right');

  const fetchVariables = useMemo(() => ({
    bookId: bookId || '',
  }), [bookId]);

  const {
    suggestions: aiSuggestions,
    isLoading: suggestionsLoading,
    refresh: refreshSuggestions,
  } = useAiSuggestions({
    fetchMutation: GENERATE_INSTRUCTION_SUGGESTIONS,
    fetchVariables,
    responseField: 'generateInstructionSuggestions',
    displayCount: 3,
    fetchCount: 15,
    enabled: showInstructionsInput !== null && !!bookId,
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setShowInstructionsInput(null);
        setInstructions('');
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setShowInstructionsInput(null);
        setInstructions('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Focus input when instructions panel opens
  useEffect(() => {
    if (showInstructionsInput && inputRef.current) {
      inputRef.current.focus();
    }
  }, [showInstructionsInput]);

  const handleMainButtonClick = () => {
    // Main button triggers the primary action based on content state
    if (hasContent && showRephraseOptions) {
      onAction('rephrase');
    } else {
      onAction('generate');
    }
  };

  const handleDropdownClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
    setShowInstructionsInput(null);
    setInstructions('');
  };

  const handleMenuItemClick = (action: AiGenerateAction) => {
    if (action === 'generateWithInstructions') {
      setShowInstructionsInput('generate');
      setInstructions('');
    } else if (action === 'rephraseWithInstructions') {
      setShowInstructionsInput('rephrase');
      setInstructions('');
    } else {
      onAction(action);
      setIsOpen(false);
    }
  };

  const handleInstructionsSubmit = () => {
    if (showInstructionsInput === 'generate') {
      onAction('generateWithInstructions', instructions);
    } else if (showInstructionsInput === 'rephrase') {
      onAction('rephraseWithInstructions', instructions);
    }
    setIsOpen(false);
    setShowInstructionsInput(null);
    setInstructions('');
  };

  const handleInstructionsKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleInstructionsSubmit();
    }
  };

  const handleSuggestionChipClick = (suggestion: string) => {
    setInstructions((prev) => {
      if (prev.trim()) {
        return prev.trim() + '. ' + suggestion;
      }
      return suggestion;
    });
  };

  const mainButtonLabel = hasContent && showRephraseOptions
    ? t('ai.rephraseWithAi')
    : t('ai.generateWithAi');

  // Size-based styles
  const sizeStyles = {
    sm: {
      button: 'gap-1 px-2 py-1 text-xs',
      dropdownButton: 'px-1 py-1',
      icon: 'w-3.5 h-3.5',
      dropdownIcon: 'w-3 h-3',
    },
    md: {
      button: 'gap-1.5 px-3 py-1.5 text-sm',
      dropdownButton: 'px-1.5 py-1.5',
      icon: 'w-4 h-4',
      dropdownIcon: 'w-4 h-4',
    },
  };

  const styles = sizeStyles[size];

  return (
    <div className={`relative inline-flex ${className}`} ref={menuRef}>
      {/* Main button with split dropdown */}
      <div ref={buttonRef} className="inline-flex rounded-lg overflow-hidden border border-transparent hover:border-primary-300 dark:hover:border-primary-700 transition-colors">
        {/* Primary action button */}
        <button
          type="button"
          onClick={handleMainButtonClick}
          disabled={disabled || isLoading}
          className={`inline-flex items-center ${styles.button} font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isLoading ? (
            <svg className={`${styles.icon} animate-spin`} fill="none" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <svg className={styles.icon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
          )}
          {mainButtonLabel}
        </button>

        {/* Dropdown toggle */}
        <button
          type="button"
          onClick={handleDropdownClick}
          disabled={disabled || isLoading}
          className={`inline-flex items-center ${styles.dropdownButton} text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border-l border-primary-200 dark:border-primary-800`}
          aria-label={t('ai.moreOptions')}
        >
          <svg
            className={`${styles.dropdownIcon} transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div ref={dropdownRef} className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 min-w-[280px]">
          {showInstructionsInput ? (
            // Instructions input panel
            <div className="p-3">
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                {showInstructionsInput === 'generate'
                  ? t('ai.instructionsForGenerate')
                  : t('ai.instructionsForRephrase')}
              </label>
              <textarea
                ref={inputRef}
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                onKeyDown={handleInstructionsKeyDown}
                placeholder={t('ai.instructionsPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
              {bookId && (
                <SuggestionChips
                  suggestions={aiSuggestions}
                  isLoading={suggestionsLoading}
                  onSelect={handleSuggestionChipClick}
                  onRefresh={refreshSuggestions}
                />
              )}
              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowInstructionsInput(null);
                    setInstructions('');
                  }}
                  className="flex-1 px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleInstructionsSubmit}
                  className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                >
                  {t('common.apply')}
                </button>
              </div>
            </div>
          ) : (
            // Menu items
            <>
              {/* Generate option */}
              <button
                type="button"
                onClick={() => handleMenuItemClick('generate')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                {hasContent ? t('ai.regenerate') : t('ai.generate')}
              </button>

              {/* Generate with instructions */}
              <button
                type="button"
                onClick={() => handleMenuItemClick('generateWithInstructions')}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
                {hasContent ? t('ai.regenerateWithInstructions') : t('ai.generateWithInstructions')}
              </button>

              {/* Rephrase options - only show if content exists and showRephraseOptions is true */}
              {hasContent && showRephraseOptions && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

                  {/* Rephrase */}
                  <button
                    type="button"
                    onClick={() => handleMenuItemClick('rephrase')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    {t('ai.rephrase')}
                  </button>

                  {/* Rephrase with instructions */}
                  <button
                    type="button"
                    onClick={() => handleMenuItemClick('rephraseWithInstructions')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                    {t('ai.rephraseWithInstructions')}
                  </button>
                </>
              )}

              {/* Open in chat */}
              {showChatOption && (
                <>
                  <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                  <button
                    type="button"
                    onClick={() => handleMenuItemClick('openInChat')}
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                    {t('ai.openInChat')}
                  </button>
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
